import { wplToken } from "@atlassiansox/wpl-token";
// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";
import { useState } from "react";
import Select, {
    components,
    ControlProps,
    DropdownIndicatorProps,
    StylesConfig
} from "react-select";
import { LanguageGlobeIcon, languages, type LanguageType } from "./common";

/**
 * Custom React-Select's Control component to allow adding the globe icon.
 */
const Control = ({ children, ...props }: ControlProps<LanguageType>) => {
    // @ts-ignore
    const { isMouseOver } = props.selectProps;

    return (
        <components.Control {...props}>
            <LanguageGlobeIcon color={isMouseOver ? "#172b4d" : "#42526e"} />
            {children}
        </components.Control>
    );
};

/**
 * Custom React-Select's DropdownIndicator component to update the arrow icon.
 */
const DropdownIndicator = (props: DropdownIndicatorProps<LanguageType>) => {
    return (
        <components.DropdownIndicator
            {...props}
            css={css`
                font-size: 1.5rem;
                height: 40px;
            `}
        >
            ▾
        </components.DropdownIndicator>
    );
};

/**
 * Styles for the LanguageSelector container div.
 */
const languageSelectorContainerStyle = css`
    display: inline-flex;
`;

/**
 * Props for the LanguageSelector component.
 */
export interface LanguageSelectorProps {
    selectedLanguage?: string;
    onLanguageChangeHandler?: React.Dispatch<string>;
    className?: string;
    testId?: string;
}

/**
 * Global footer's language selector component.
 * Uses React-Select to render the language selector with custom styling and on mouseover behavior.
 * Dropdown menu and the options have the same styling as WPL DropdownSelector.
 *
 * @param selectedLanguage - The selected language. Defaults to 'en'.
 * @param onChangeHandler - The function to call when a different language is selected.
 * @param className - The class name to apply to the container div.
 */
const LanguageSelector = ({
    selectedLanguage = "en",
    onLanguageChangeHandler,
    className,
    testId
}: LanguageSelectorProps) => {
    const [isMouseOver, setMouseOver] = useState(false);

    const languageSelectStyle: StylesConfig<LanguageType> = {
        container: (provided) => ({
            ...provided,
            appearance: "none",
            flexDirection: "row",
            fontSize: "0.875rem;",
            textAlign: "left"
        }),
        control: (provided, state) => ({
            ...provided,
            color: isMouseOver ? "#172b4d" : "#42526e",
            cursor: isMouseOver ? "pointer" : "default",
            display: "inline-flex",
            border: state.isFocused
                ? `2px solid #4c9aff`
                : "2px solid transparent"
        }),
        valueContainer: (provided) => ({
            ...provided,
            marginLeft: wplToken("space.050"),
            textDecorationLine: isMouseOver ? "underline" : "none"
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            marginLeft: wplToken("space.050")
        }),
        menu: (provided) => ({
            ...provided,
            marginTop: wplToken("space.050"),
            boxShadow: "0px 3px 5px 0px #091E4233",
            background: "#ffffff",
            borderRadius: wplToken("space.050"),
            width: "5.5rem"
        }),
        option: (provided, state) => ({
            ...provided,
            "cursor": "pointer",
            "padding": `${wplToken("space.100")} ${wplToken("space.100")}`,
            "backgroundColor": state.isSelected
                ? "#b2d4ff"
                : state.isFocused
                ? "#ebecf0"
                : "#fafbfc",
            "color": state.isSelected ? "#0065ff" : "#172b4d",
            "&:hover": {
                background: state.isSelected ? "#b2d4ff" : "#ebecf0"
            },
            "&:active": {
                background: "#dfe1e5"
            }
        })
    };

    return (
        <div
            className={className}
            data-testid={testId}
            css={languageSelectorContainerStyle}
            onMouseEnter={() => setMouseOver(true)}
            onMouseLeave={() => setMouseOver(false)}
        >
            <Select<LanguageType>
                defaultValue={languages.find(
                    (language) => language.value === selectedLanguage
                )}
                options={languages}
                onChange={(selectedOption) => {
                    selectedOption &&
                        selectedOption.value !== selectedLanguage &&
                        onLanguageChangeHandler?.(selectedOption.value);
                }}
                unstyled={true}
                // @ts-ignore
                isMouseOver={isMouseOver}
                onMenuClose={() => setMouseOver(false)}
                components={{ Control, DropdownIndicator }}
                styles={languageSelectStyle}
                menuPlacement="auto"
            />
        </div>
    );
};

export default LanguageSelector;
